/* eslint-disable react-hooks/exhaustive-deps */
import { Autocomplete, Box, createFilterOptions, InputAdornment, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Card, CircularProgress, Grid, Typography } from 'apollo-react';
import IconButton from 'apollo-react/components/IconButton';
import Tooltip from 'apollo-react/components/Tooltip';
import Search from 'apollo-react-icons/Search';
import classnames from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import {
  setClearTextOptions,
  setSearchText,
  setSearchTextOptions,
} from '../../actions/TopMenuActions';
import { getEnhancedGlobalRecordNumberSearch } from '../../api/global-search';
import { DEBOUNCE_SEARCH_TIME } from '../../constants/Common';
import { INPUT_ICON_DEFAULT_SIZE } from '../../constants/CommonStylesConstants';
import { getIsRtlLanguage } from '../../selectors/AuthSelectors';
import withTranslation from '../withTranslation';

const useStyles = makeStyles(({ palette }) => ({
  root: {
    margin: 0,
    width: 215,
  },
  inputRoot: {
    margin: '0 !important',
  },
  input: {
    paddingLeft: '40px !important',
    border: 'unset !important',
    outline: 'unset !important',
  },
  rtlInput: {
    paddingRight: '50px !important',
    border: 'unset !important',
    outline: 'unset !important',
  },
  searchIcon: {
    color: palette?.primary?.main,
    '&:hover': {
      cursor: 'pointer',
    },
  },
  iconSize: INPUT_ICON_DEFAULT_SIZE,
  rtlSearchIcon: {
    color: '#999999',
    marginRight: '6px',
  },
  property: {
    color: palette?.grey[800],
  },
  cart: {
    marginBottom: 5,
    marginRight: 5,
    marginLeft: 5,
    cursor: 'pointer',
  },
  paperComponent: {
    borderRadius: '4px',
    backgroundColor: 'white',
    maxHeight: '200px',
  },
  autocomplete: {
    '& .MuiOutlinedInput-root': {
      height: '44px',
      paddingRight: '0px !important',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
      outline: 'none !important',
    },
  },
}));
const SearchIcon = ({ classes, translateString, enableSearch, isRtlLanguage, onSearchClick }) => {
  return (
    <Tooltip title={translateString('Search')} placement="bottom">
      <IconButton aria-label="Search" color="primary" size="medium" onClick={onSearchClick}>
        <Search
          className={[
            classnames({
              [classes.iconSize]: true,
              [classes.searchIcon]: enableSearch,
              [classes.rtlSearchIcon]: isRtlLanguage,
            }),
          ]}
        />
      </IconButton>
    </Tooltip>
  );
};
const SearchInput = ({
  enableSearch,
  onSearch,
  translateString,
  dropDownWidth,
  entitySelected,
}) => {
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { search } = useLocation();

  const value = useSelector((state) => state.topMenu.searchText);
  const pageNumber = useSelector((state) => state.topMenu.pageNumber);
  const isRtlLanguage = useSelector(getIsRtlLanguage);

  const options = useSelector((state) => state.topMenu.searchTextOptions ?? []);

  const autocompleteRef = useRef(null);
  const observer = useRef(null);
  const lastObservedItemRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [prevEntitySelected, setPrevEntitySelected] = useState(entitySelected);

  const isAutocompleteEnabled = value?.length >= 3;
  const translatedSearchFor = translateString('Search For');
  const ellipsisWidth = dropDownWidth - 140;

  const query = new URLSearchParams(search);
  const itmId = query.get('itmId');
  const selectedOption = options.find((opt) => opt.ObjId === itmId) || null;

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      onSearch();
    }
    if (event.key === 'ArrowDown' && isLoading) {
      event.stopPropagation();
      return;
    }
  };

  const filterOptions = createFilterOptions({
    trim: true,
  });

  const fetchOptions = async (value, newPageNumber, entitySelected) => {
    setIsLoading(true);
    const trimValue = value.trim();
    const entitySelectedName = entitySelected === 'ALL' ? [] : [entitySelected];
    const result = await getEnhancedGlobalRecordNumberSearch({
      SearchRecordNumber: trimValue,
      PageNumber: newPageNumber,
      PageSize: 30,
      SearchOperator: 'CT',
      EntityIds: entitySelectedName,
    });
    const newOptions = result?.EnhancedGlobalRecordNumberSearchData.Rows || [];
    const defaultOption = {
      isDefault: true,
      RecordNumber: `${translatedSearchFor} "${trimValue}"`,
      searchValue: trimValue,
    };
    if (newOptions.length >= 1) {
      if (newPageNumber === 1) {
        dispatch(setSearchTextOptions([defaultOption, ...newOptions]));
      } else {
        dispatch(setSearchTextOptions(newOptions));
      }
    }
    setIsLoading(false);
  };

  const lastOptionRef = useCallback(
    (node) => {
      if (isLoading) {
        return;
      }
      if (observer.current) {
        observer.current.disconnect();
      }
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (lastObservedItemRef.current !== node) {
            lastObservedItemRef.current = node;
            fetchOptions(value, pageNumber, entitySelected);
          }
        }
      });
      if (node) {
        observer.current.observe(node);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading, fetchOptions, value, entitySelected]
  );

  const highlightMatch = (text, query) => {
    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1 || query === '') {
      return text;
    }
    return (
      <>
        {text.substring(0, index)}
        <strong>{text.substring(index, index + query.length)}</strong>
        {text.substring(index + query.length)}
      </>
    );
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    dispatch(setSearchText(value));
    if (value.length >= 3) {
      setIsLoading(true);
      debouncedFetchOptions(value, 1, entitySelected);
    } else {
      dispatch(setClearTextOptions());
    }
  };

  useEffect(() => {
    if (entitySelected !== prevEntitySelected && isAutocompleteEnabled) {
      dispatch(setClearTextOptions());
      fetchOptions(value, 1, entitySelected);
      setPrevEntitySelected(entitySelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entitySelected]);

  const debouncedFetchOptions = useCallback(
    _.debounce((value, newPageNumber, entitySelected) => {
      fetchOptions(value, newPageNumber, entitySelected);
    }, DEBOUNCE_SEARCH_TIME),
    [translatedSearchFor]
  );

  return (
    <Box width={230}>
      <Autocomplete
        ref={autocompleteRef}
        filterOptions={filterOptions}
        inputValue={value}
        freeSolo={!isAutocompleteEnabled}
        loading={isLoading}
        popupIcon={false}
        noOptionsText={translateString('No options found')}
        id="Autocomplete"
        disableClearable
        className={classes.autocomplete}
        options={value?.length >= 3 ? options : []}
        value={selectedOption}
        getOptionLabel={(option) =>
          `${option.RecordNumber} ${option.RevisionNumber ? `[${option.RevisionNumber}]` : ''}`
        }
        isOptionEqualToValue={(option, value) =>
          option.RecordNumber === value.RecordNumber &&
          option.RevisionNumber === value.RevisionNumber
        }
        onChange={(_, value) => {
          if (value?.EntityId) {
            history.push(
              `entity-display?entityType=${value.EntityId}&formKey=${value.EntityId}_DETAIL&itmId=${value.ObjId}`
            );
          } else if (value?.isDefault) {
            onSearch();
          }
        }}
        renderOption={(props, option, { index }) => (
          <Box
            ref={index === options.length - 1 ? lastOptionRef : null}
            className={classes.paperComponent}
          >
            <Card className={classes.cart} variant="outlined">
              <Grid container {...props} spacing={0} alignItems="center">
                <Grid item xs={12}>
                  <Typography className={classes.property} variant="bodySmall">
                    {highlightMatch(option.RecordNumber, value.trim())}
                    {option.RevisionNumber && `\u00A0[${option.RevisionNumber}]`}
                  </Typography>
                </Grid>
                <Grid item xs={12} container alignItems="center">
                  <Box display="flex">
                    <Typography className={classes.property} variant="caption">
                      {option.TranslatedEntityId}
                    </Typography>
                    {option.Title && (
                      <>
                        <Typography
                          noWrap={true}
                          className={classes.property}
                          variant="caption"
                          maxWidth={ellipsisWidth}
                        >
                          {'\u00A0|\u00A0'}
                          {option.Title}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            type="search"
            onKeyDown={enableSearch ? handleKeyPress : undefined}
            onChange={handleInputChange}
            placeholder={translateString('Search')}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment sx={{ marginLeft: '0' }} position="start">
                  {isLoading ? (
                    <div style={{ marginLeft: '14px' }}>
                      <CircularProgress variant="indeterminate" size={20} />
                    </div>
                  ) : (
                    <SearchIcon
                      classes={classes}
                      translateString={translateString}
                      enableSearch={enableSearch}
                      isRtlLanguage={isRtlLanguage}
                      onSearchClick={enableSearch ? onSearch : () => {}}
                    />
                  )}
                </InputAdornment>
              ),
              classes: { input: isRtlLanguage ? classes.rtlInput : classes.input },
            }}
          />
        )}
        componentsProps={{
          paper: {
            sx: {
              width: dropDownWidth,
              marginTop: '3px',
            },
          },
        }}
        slotProps={{
          popper: {
            sx: {
              zIndex: 3001,
            },
          },
        }}
      />
    </Box>
  );
};
export default withTranslation(SearchInput);
